import React from "react";
import { Link, useHistory } from "react-router-dom";
import { Dropdown, Popover, Whisper, Table, Badge, Tooltip } from "rsuite";
import { AppRouteUi, AppRouteApi } from "../config/appRoute";
import { ComponentNameType } from "../models/componentName";
import { Image } from "./image.component";
import { CustomModal } from "./modal/customModal.component";
import editIcon from "../assets/img/edit-icon.svg";
import eyeIcon from "../assets/img/eye-icon.svg";
import { UtilityDate } from "../utils/dateUtils";
import { AddTaskSchema } from "../models/api";
import { ApiSchemaInsuranceType } from "../models/api/insuranceType";
import { ModalData } from "./modal";
import { useDispatch } from "react-redux";
import { ActionsModal } from "../core/redux/slices/sliceModal";
import { EnumModals } from "../libs";
import { PatientTaskUtils } from "../utils";
import { FaListUl, FaCalendarAlt, FaCalendarDay } from "react-icons/fa";

export const TableLink = ({
  sKey,
  component,
  render,
  rowData,
  passState,
}: {
  sKey: string;
  component?: ComponentNameType;
  render: string | JSX.Element;
  passState?: boolean;
  rowData?: any;
}) => {
  const history = useHistory();
  return passState && rowData ? (
    <span
      onClick={() => {
        component &&
          history.push(`${AppRouteUi[component]?.Root()}/${sKey}`, rowData);
      }}
    >
      {render}
    </span>
  ) : (
    <Link
      className="text-text-decoration-none"
      to={
        component && ["Appointment", "ManageTaskList"].includes(component)
          ? "#"
          : component
          ? `${AppRouteUi[component]?.Root()}/${sKey}`
          : "#"
      }
    >
      {render}
    </Link>
  );
};

export function DateCell<T extends { [key: string]: string }>({
  rowData,
  dataKey,
  component,
  time,
  ...props
}: {
  rowData?: T;
  dataKey?: keyof T;
  time?: boolean;
  component?: ComponentNameType;
  previousTimeKey?: string;
}) {
  const dispatch = useDispatch();

  const content =
    rowData && dataKey
      ? time
        ? UtilityDate.formatTime(new Date(rowData[dataKey]))
        : UtilityDate.formatDate(rowData[dataKey] as unknown as Date)
      : "";

  const previousTime = props.previousTimeKey
    ? rowData?.[props.previousTimeKey]
    : "";
  const handleClick = () => {
    if (!previousTime) return;
    const previousTimeStr = UtilityDate.formatTime(new Date(previousTime));
    ModalData.CommonModal.headerText = "Previous time";
    ModalData.CommonModal.body = (
      <div className="w-100 text-center py-2">
        <span>{previousTimeStr}</span>
      </div>
    );
    dispatch(ActionsModal.openModal(EnumModals.COMMON_MODAL));
  };
  return (
    <Table.Cell {...props}>
      <p
        className={`${
          previousTime ? "border border-success rounded-pill click" : ""
        }`}
        title={content}
        onClick={handleClick}
      >
        {content}
      </p>
    </Table.Cell>
  );
}

export function ImageCell<T extends { [key: string]: string }>({
  rowData,
  dataKey,
  component,
  ...props
}: {
  rowData?: T;
  dataKey?: keyof T;
  component?: ComponentNameType;
}) {
  return (
    <Table.Cell {...props}>
      {rowData && dataKey && (
        <Image
          key={`profile_${rowData[dataKey]}`}
          src={
            rowData[dataKey]
              ? AppRouteApi.Media.ByPath(rowData[dataKey])
              : "/img/admin-profile.svg"
          }
          alt={`profile_${rowData[dataKey]}`}
          height={25}
          width={25}
          className="rounded-circle object-fit-cover"
        />
      )}
    </Table.Cell>
  );
}

export function TextCell<T extends { [key: string]: string }>({
  rowData,
  dataKey,
  component,
  ...props
}: {
  rowData?: T;
  dataKey?: keyof T;
  component?: ComponentNameType;
}) {
  return (
    <Table.Cell {...props}>
      {rowData && dataKey && (
        <p title={rowData[dataKey]}>{rowData[dataKey] || ""}</p>
      )}
    </Table.Cell>
  );
}

export function NumberCell<T extends { [key: string]: Number }>({
  rowData,
  dataKey,
  component,
  ...props
}: {
  rowData?: T;
  dataKey?: keyof T;
  component?: ComponentNameType;
}) {
  return (
    <Table.Cell {...props}>
      {rowData && dataKey && (
        <p title={`${rowData[dataKey]}`}>{`${rowData[dataKey]}` || ""}</p>
      )}
    </Table.Cell>
  );
}

export function FileCell<T extends { [key: string]: string }>({
  rowData,
  component,
  dataKey,
  children,
  ...props
}: {
  rowData?: T;
  dataKey?: keyof T;
  component?: ComponentNameType;
  children: JSX.Element;
}) {
  return (
    <Table.Cell {...props}>
      {rowData && dataKey && (
        <a href={AppRouteApi.Media.ByPath(rowData[dataKey][0])} download>
          {children}
        </a>
      )}
    </Table.Cell>
  );
}

type MenuPopoverProps = {
  onSelect: (eventKey: string, event: any) => void;
  onClose: () => void;
  component?: ComponentNameType;
  isDeactivated?: boolean;
  hideDelete?: boolean;
} & { [key: string]: unknown };

export const MenuPopover = React.forwardRef<HTMLDivElement, MenuPopoverProps>(
  ({ onSelect, onClose, component, isDeactivated, ...rest }, ref) => (
    <Popover {...rest} ref={ref} full>
      <Dropdown.Menu onSelect={onSelect}>
        <Dropdown.Item eventKey="update" onClick={onClose}>
          Update
        </Dropdown.Item>
        {!rest.hideDelete && (
          <Dropdown.Item eventKey="delete" onClick={onClose}>
            Delete
          </Dropdown.Item>
        )}
        {!!component &&
          (component === "Patient" || component === "Caregiver") && (
            <Dropdown.Item eventKey="changeStatus" onClick={onClose}>
              {isDeactivated ? "Activate" : "Deactivate"}
            </Dropdown.Item>
          )}
      </Dropdown.Menu>
    </Popover>
  )
);

export function ActionCell<T extends { sKey: string; isDeActivated: boolean }>({
  rowData,
  component,
  triggerRef,
  handleSelectMenu,
  hideViewBtn,
  hideEditDeleteBtn,
  passState,
  ...props
}: {
  rowData?: T;
  component?: ComponentNameType;
  triggerRef: React.Ref<HTMLDivElement>;
  handleSelectMenu: (
    eventKey: string,
    event: Event,
    sKey?: string,
    rowData?: any,
    isDeactivated?: boolean
  ) => void;
  hideViewBtn?: boolean;
  hideEditDeleteBtn?: boolean;
  passState?: boolean;
}) {
  return (
    <Table.Cell {...props}>
      {rowData && !hideViewBtn && (
        <TableLink
          sKey={rowData.sKey}
          component={component}
          render={
            <img
              src={eyeIcon}
              alt="View"
              title="View"
              className="me-2 click"
              height={20}
            />
          }
          passState={passState}
          rowData={rowData}
        />
      )}
      {rowData && !hideEditDeleteBtn && (
        <Whisper
          placement="left"
          trigger="click"
          ref={triggerRef}
          speaker={(props, ref) => {
            const { className, left, top, onClose } = props;
            return (
              <MenuPopover
                ref={ref}
                className={className}
                style={{ left, top }}
                onClose={onClose}
                onSelect={(eventKey: string, event: Event) =>
                  handleSelectMenu(
                    eventKey,
                    event,
                    rowData.sKey,
                    rowData,
                    rowData.isDeActivated
                  )
                }
                component={component}
                isDeactivated={rowData.isDeActivated}
              />
            );
          }}
        >
          <img
            src={editIcon}
            alt="Update/Delete"
            className="me-1 click"
            height={15}
          />
        </Whisper>
      )}
    </Table.Cell>
  );
}

export const SeeTasksCell = ({
  taskDetailsList,
  isDHS,
  ...props
}: {
  taskDetailsList?: AddTaskSchema[];
  isDHS?: boolean;
}) => {
  const monthlyTask = taskDetailsList?.filter((task) => task.isMonthly) || [];
  const weeklyTask = taskDetailsList?.filter((task) => !task.isMonthly) || [];
  
  const totalTaskCount = monthlyTask.length + weeklyTask.length;
  
  // Get color based on task count
  const getTaskCountColor = () => {
    if (totalTaskCount === 0) return '#6c757d'; // gray
    if (totalTaskCount <= 2) return '#17a2b8'; // info
    if (totalTaskCount <= 5) return '#28a745'; // success
    return '#fd7e14'; // warning
  };

  // Get tooltip content
  const getTooltipContent = () => {
    if (totalTaskCount === 0) return 'No tasks assigned';
    
    let content = '';
    if (weeklyTask.length > 0) content += `${weeklyTask.length} Weekly Task${weeklyTask.length > 1 ? 's' : ''}\n`;
    if (monthlyTask.length > 0) content += `${monthlyTask.length} Monthly Task${monthlyTask.length > 1 ? 's' : ''}`;
    return content;
  };

  return (
    <CustomModal
      component={
        <Whisper
          trigger="hover"
          placement="left"
          speaker={<Tooltip>{getTooltipContent()}</Tooltip>}
        >
          <div className="d-flex align-items-center" style={{ cursor: "pointer" }}>
            <FaListUl size={16} style={{ color: getTaskCountColor() }} />
            {weeklyTask.length > 0 && (
              <div className="ms-2">
                <Badge content={weeklyTask.length}>
                  <FaCalendarDay size={12} style={{ opacity: 0.7 }} />
                </Badge>
              </div>
            )}
            {monthlyTask.length > 0 && (
              <div className="ms-2">
                <Badge content={monthlyTask.length}>
                  <FaCalendarAlt size={12} style={{ opacity: 0.7 }} />
                </Badge>
              </div>
            )}
            {totalTaskCount === 0 && (
              <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>None</span>
            )}
          </div>
        </Whisper>
      }
      title="Patient Tasks"
    >
      {weeklyTask.length > 0 && (
        <div>
          <div className="d-flex align-items-center mb-2">
            <FaCalendarDay className="text-primary me-2" />
            <label className="text-primary mb-0 fw-bold">Weekly Tasks</label>
          </div>
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ width: 30 }}>Task</th>
                <th style={{ width: 40 }}>Days Per Week</th>
                <th style={{ width: 20 }}>Minutes Per Day</th>
              </tr>
            </thead>
            <tbody>
              {weeklyTask.map((task, index) => (
                <tr key={index}>
                  <td>
                    {task.patientTask?.displayName ||
                      task.patientTask?.name ||
                      "N/A"}
                  </td>
                  <td>{task.weeklyAvailabilities?.join(", ")}</td>
                  <td>
                    {PatientTaskUtils.formatSecondToMinute(task.secondsPerDay)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {monthlyTask.length > 0 && (
        <div>
          <div className="d-flex align-items-center mb-2 mt-3">
            <FaCalendarAlt className="text-primary me-2" />
            <label className="text-primary mb-0 fw-bold">Monthly Tasks</label>
          </div>
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ width: 30 }}>Task</th>
                <th style={{ width: 40 }}>Total Per Month</th>
                <th style={{ width: 20 }}>Minutes Per Day</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTask.map((task, index) => (
                <tr key={index}>
                  <td>
                    {task.patientTask?.displayName ||
                      task.patientTask?.name ||
                      "N/A"}
                  </td>
                  <td>{task.totalTaskPerMonth}</td>
                  <td>
                    {PatientTaskUtils.formatSecondToMinute(task.secondsPerDay)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalTaskCount === 0 && (
        <div className="text-center text-secondary p-4">
          <FaListUl size={32} className="mb-3" style={{ opacity: 0.5 }} />
          <p>This patient has no assigned tasks</p>
        </div>
      )}
    </CustomModal>
  );
};

export const SeeInsuranceTypesCell = ({
  insuranceTypeList,
}: {
  insuranceTypeList?: ApiSchemaInsuranceType[];
}) => {
  return (
    <CustomModal
      component={
        <button type="button" className="btn btn-xs btn-green">
          See Insurance Types
        </button>
      }
      title="Insurance Type List"
    >
      <div>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Subtitle</th>
              <th>Insurance Type</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {insuranceTypeList &&
              insuranceTypeList.map((insuranceType, index) => (
                <tr key={index}>
                  <td>{insuranceType?.subtitle || "N/A"}</td>
                  <td>
                    {insuranceType?.displayName || insuranceType?.name || "N/A"}
                  </td>

                  <td>
                    {insuranceType.isDisabled && (
                      <Badge
                        content={"Disabled"}
                        style={{
                          background: "#FF0000",
                        }}
                        className="p-1 px-2"
                      />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </CustomModal>
  );
};
